import { ServerResponse } from 'http'

import { CacheControl, format } from '@tusbar/cache-control'
import { NextPageContext } from 'next'
import { AppContext, AppInitialProps } from 'next/app'

// Multiplier for content that can be updated rapidly
const volatilityMultiplier = 0.1
// Allow stale content for 5x the time we allow for content to cache normally.
const staleWhileRevalidateMultiplier = 5
// Allow stale content rather than an error for 10x the time we allow for content to cache normally.
const staleIfErrorMultiplier = 10

// Default TTL for html if not set in env (10 mins)
const defaultHtmlTTL = 600
// Default TTL for static assets if not set in env (1 month)
const defaultStaticAssetsTTL = 2592000

export interface WithResponseCacheControl {
  responseCacheControl: CacheControl
}

type CachePolicyKeys =
  | 'noCache'
  | 'publicHtmlPolicy'
  | 'volatileSharedHtmlPolicy'
  | 'staticAssetsPolicy'

/**
 * Our cached HTML still varies based on the Cookies of a user.
 *
 * The CDN supports splitting caches by specific cookies. Browser
 * caches do not yet support this. So we can only cache our HTML
 * in the CDN to avoid browsers reusing an HTML document based on
 * its URL even though the Cookies for preferred language have changed.
 */
export const cachePolicies: Record<CachePolicyKeys, CacheControl> = {
  noCache: { noStore: true, public: false, maxAge: 0, sharedMaxAge: 0 },
  publicHtmlPolicy: {
    public: true,
    maxAge: 0,
    sharedMaxAge: parseInt(process.env.PUBLIC_CACHE_HTML_TTL ?? `${defaultHtmlTTL}`),
    staleWhileRevalidate:
      parseInt(process.env.PUBLIC_CACHE_HTML_TTL ?? `${defaultHtmlTTL}`) *
      staleWhileRevalidateMultiplier,
    staleIfError:
      parseInt(process.env.PUBLIC_CACHE_HTML_TTL ?? `${defaultHtmlTTL}`) * staleIfErrorMultiplier,
  },
  volatileSharedHtmlPolicy: {
    public: true,
    maxAge: 0,
    sharedMaxAge:
      parseInt(process.env.PUBLIC_CACHE_HTML_TTL ?? `${defaultHtmlTTL}`) * volatilityMultiplier,
    staleWhileRevalidate:
      parseInt(process.env.PUBLIC_CACHE_HTML_TTL ?? `${defaultHtmlTTL}`) *
      volatilityMultiplier *
      staleWhileRevalidateMultiplier,
    staleIfError:
      parseInt(process.env.PUBLIC_CACHE_HTML_TTL ?? `${defaultHtmlTTL}`) * staleIfErrorMultiplier,
  },
  staticAssetsPolicy: {
    public: true,
    maxAge: parseInt(process.env.PUBLIC_CACHE_STATIC_ASSETS_TTL ?? `${defaultStaticAssetsTTL}`),
    sharedMaxAge: parseInt(
      process.env.PUBLIC_CACHE_STATIC_ASSETS_TTL ?? `${defaultStaticAssetsTTL}`
    ),
    staleWhileRevalidate:
      parseInt(process.env.PUBLIC_CACHE_STATIC_ASSETS_TTL ?? `${defaultStaticAssetsTTL}`) *
      staleWhileRevalidateMultiplier,
    staleIfError:
      parseInt(process.env.PUBLIC_CACHE_STATIC_ASSETS_TTL ?? `${defaultStaticAssetsTTL}`) *
      staleIfErrorMultiplier,
  },
}

export function conditionallySetCacheControl(appContext: AppContext, appProps: AppInitialProps) {
  if (!viableCacheContext(appContext.ctx)) {
    return
  }

  const { pageProps } = appProps

  if (typeof pageProps.responseCacheControl !== 'undefined') {
    setCacheControl(appContext.ctx.res, pageProps.responseCacheControl)
  }
}

function viableCacheContext(ctx: NextPageContext) {
  return ctx.res && !ctx.err
}

function setCacheControl(res: ServerResponse | undefined, cc: CacheControl) {
  if (res?.writableEnded) return
  res?.setHeader('Cache-Control', format(cc))
}
